import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Grid,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import { 
  PictureAsPdf, 
  Assessment, 
  TrendingUp, 
  Inventory,
  People,
  GetApp,
  Schedule
} from '@mui/icons-material';
import { DESIGN_SYSTEM } from '../../../theme/designSystem';

/**
 * Widget de Acceso Rápido a Reportes
 */
export default function ReportsAccessCard() {
  const theme = useTheme();

  const reportTypes = [
    {
      name: 'Reporte de Ventas',
      icon: <TrendingUp />,
      color: theme.palette.primary.main,
      description: 'Análisis completo de ventas'
    },
    {
      name: 'Reporte de Productos',
      icon: <Inventory />,
      color: theme.palette.success.main,
      description: 'Rendimiento de productos'
    },
    {
      name: 'Reporte de Clientes',
      icon: <People />,
      color: theme.palette.info.main,
      description: 'Segmentación y comportamiento'
    }
  ];

  const recentReports = [
    { name: 'Ventas - Julio 2025', date: '15 Jul', status: 'Listo' },
    { name: 'Productos - Q2', date: '30 Jun', status: 'Listo' },
    { name: 'Ejecutivo - Q2', date: '28 Jun', status: 'Listo' }
  ];

  return (
    <Card sx={{
      height: 400,
      background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.02)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
      border: `1px solid ${alpha(theme.palette.secondary.main, 0.08)}`,
      borderRadius: 3,
      boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.12)}`
      }
    }}>
      <CardContent sx={{ p: 3, height: '100%' }}>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <PictureAsPdf sx={{ color: theme.palette.secondary.main, fontSize: 24 }} />
            <Typography sx={DESIGN_SYSTEM.typography.cardTitle}>
              Reportes PDF
            </Typography>
          </Box>
          <Chip label="Premium" size="small" color="secondary" />
        </Box>

        {/* Tipos de Reportes */}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Generar Reportes:
        </Typography>
        
        <Grid container spacing={1} sx={{ mb: 3 }}>
          {reportTypes.map((report, index) => (
            <Grid item xs={4} key={index}>
              <Box 
                sx={{
                  p: 1.5,
                  border: `1px solid ${alpha(report.color, 0.2)}`,
                  borderRadius: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: alpha(report.color, 0.05),
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                <Box sx={{ color: report.color, mb: 0.5 }}>
                  {report.icon}
                </Box>
                <Typography variant="caption" fontWeight={600}>
                  {report.name.split(' ')[0]}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Reportes Recientes */}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Reportes Recientes:
        </Typography>
        
        <List dense sx={{ mb: 2 }}>
          {recentReports.map((report, index) => (
            <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <PictureAsPdf fontSize="small" color="action" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight={500}>
                    {report.name}
                  </Typography>
                }
                secondary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" color="text.secondary">
                      {report.date}
                    </Typography>
                    <Chip 
                      label={report.status} 
                      size="small" 
                      color="success"
                      sx={{ height: 16, fontSize: '0.65rem' }}
                    />
                  </Box>
                }
              />
              <Button size="small" startIcon={<GetApp />}>
                Descargar
              </Button>
            </ListItem>
          ))}
        </List>

        {/* Botón Principal */}
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          startIcon={<Assessment />}
          sx={{ mt: 'auto' }}
        >
          Ver Todos los Reportes
        </Button>
      </CardContent>
    </Card>
  );
}
